import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { Alert, Badge, Button, Spinner } from 'react-bootstrap';

import {
  GachaBoxType,
  GachaShopConfigDataFragment,
  SetGachaPinnedBoxMutation,
  SetGachaPinnedBoxMutationVariables,
} from '../../../../../../__gqltypes__';
import { SET_GACHA_PINNED_BOX } from '../graphql';
import { GACHA_CURRENCY_LABELS, GachaBoxDraft, GachaCampaignData, reorderList } from '../utils';

type Props = {
  campaignId: string;
  boxes: GachaBoxDraft[];
  selectedClientId: string | null;
  onSelect: (clientId: string) => void;
  onBoxesChange: (boxes: GachaBoxDraft[]) => void;
  onAddBox: () => void;
  allCampaigns: GachaCampaignData[];
  shopConfig: GachaShopConfigDataFragment | null;
  onShopConfigChange: (config: GachaShopConfigDataFragment) => void;
};

function boxSummary(box: GachaBoxDraft): string {
  if (box.type === GachaBoxType.FREE) {
    return 'Free claim';
  }
  const price = `${box.priceAmount} ${GACHA_CURRENCY_LABELS[box.priceCurrency]}`;
  return box.discountEnabled ? `${price}, -${box.discountPercent}%` : price;
}

// Where the single global pin is, in words ("Campaign X, box 2 (GOLD)"), or null when nothing is pinned.
export function describeGachaPin(
  shopConfig: GachaShopConfigDataFragment | null,
  campaigns: GachaCampaignData[]
): string | null {
  if (!shopConfig?.pinnedBoxId) {
    return null;
  }
  const campaign = campaigns.find((candidate) => candidate.id === shopConfig.pinnedCampaignId);
  const index = campaign?.boxes.findIndex((box) => box.id === shopConfig.pinnedBoxId) ?? -1;
  if (!campaign || index < 0) {
    return 'a box that no longer exists (it is ignored: the carousel falls back to its first box)';
  }
  return `${campaign.name}, box ${index + 1} (${campaign.boxes[index].tier})`;
}

/**
 * The campaign's boxes in carousel order (level 2 = list order, saved with the campaign), drag to reorder, and
 * the pin star: one global pin across every campaign, saved immediately (not with the campaign).
 */
export function GachaBoxList({
  campaignId,
  boxes,
  selectedClientId,
  onSelect,
  onBoxesChange,
  onAddBox,
  allCampaigns,
  shopConfig,
  onShopConfigChange,
}: Props): JSX.Element {
  const [pinError, setPinError] = useState<string | null>(null);
  const [setPinnedBox, { loading: pinning }] = useMutation<
    SetGachaPinnedBoxMutation,
    SetGachaPinnedBoxMutationVariables
  >(SET_GACHA_PINNED_BOX);

  const pinnedHere = shopConfig?.pinnedCampaignId === campaignId ? shopConfig?.pinnedBoxId ?? null : null;
  const pinDescription = describeGachaPin(shopConfig, allCampaigns);

  const togglePin = (box: GachaBoxDraft) => {
    if (!box.id) {
      return;
    }
    const unpin = pinnedHere === box.id;
    setPinError(null);
    setPinnedBox({ variables: unpin ? { campaignId: null, boxId: null } : { campaignId, boxId: box.id } })
      .then(({ data }) => {
        if (data?.setGachaPinnedBox) {
          onShopConfigChange(data.setGachaPinnedBox);
        }
      })
      .catch((error) => setPinError(error.message));
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }
    onBoxesChange(reorderList(boxes, result.source.index, result.destination.index));
  };

  return (
    <div>
      <div className="small mb-2">
        <i className="fas fa-star text-warning mr-1" />
        Pinned default box (one for the whole shop):{' '}
        {pinDescription ?? <em>none, the carousel opens on its first box</em>}
        {pinning && <Spinner animation="border" size="sm" className="ml-2" />}
      </div>
      {pinError && (
        <Alert variant="danger" dismissible onClose={() => setPinError(null)}>
          {pinError}
        </Alert>
      )}
      {boxes.length === 0 && <div className="text-muted mb-2">No box yet.</div>}
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId={`gacha-boxes-${campaignId}`}>
          {(droppable) => (
            <div className="list-group mb-2" {...droppable.droppableProps} ref={droppable.innerRef}>
              {boxes.map((box, index) => (
                <Draggable key={box._clientId} draggableId={box._clientId} index={index}>
                  {(draggable) => (
                    <div
                      ref={draggable.innerRef}
                      {...draggable.draggableProps}
                      {...draggable.dragHandleProps}
                      role="button"
                      tabIndex={0}
                      onClick={() => onSelect(box._clientId)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          onSelect(box._clientId);
                        }
                      }}
                      className={[
                        'list-group-item list-group-item-action py-2',
                        'd-flex justify-content-between align-items-center',
                        box._clientId === selectedClientId ? 'active' : '',
                      ].join(' ')}
                    >
                      <div>
                        <i className="fas fa-grip-vertical text-muted mr-2" />
                        <strong>Box {index + 1}</strong> <Badge variant="info">{box.tier}</Badge>{' '}
                        <Badge variant={box.type === GachaBoxType.PAID ? 'warning' : 'success'}>{box.type}</Badge>{' '}
                        <span className="small">{boxSummary(box)}</span>
                        {box.mystery?.enabled && (
                          <Badge variant="dark" className="ml-1">
                            Mystery
                          </Badge>
                        )}
                        {!box.id && (
                          <Badge variant="secondary" className="ml-1">
                            New (unsaved)
                          </Badge>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="link"
                        className="p-0"
                        disabled={!box.id || pinning}
                        title={
                          !box.id
                            ? 'Save the campaign before pinning a new box'
                            : pinnedHere === box.id
                            ? 'Unpin (the carousel opens on its first box)'
                            : 'Pin as the default box (replaces the current pin)'
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePin(box);
                        }}
                      >
                        <i className={`${pinnedHere === box.id ? 'fas' : 'far'} fa-star text-warning`} />
                      </Button>
                    </div>
                  )}
                </Draggable>
              ))}
              {droppable.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <Button size="sm" variant="outline-primary" onClick={onAddBox}>
        <i className="fas fa-plus mr-1" />
        Add a box
      </Button>
      <span className="small text-muted ml-2">Drag to reorder; the order is saved with the campaign.</span>
    </div>
  );
}
